import React from "react";
import { useImportProgress } from "../contexts/ImportProgressContext";
import { useNavigate } from "react-router-dom";

/* Inline keyframes — avoids touching tailwind.config.js */
const slideInStyle = document.createElement("style");
slideInStyle.textContent = `@keyframes bgImportSlideIn { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: translateY(0); } }`;
if (!document.head.querySelector("[data-bg-import]")) {
  slideInStyle.setAttribute("data-bg-import", "");
  document.head.appendChild(slideInStyle);
}

/**
 * Circular progress indicator that fills from 0% to 100%.
 * Uses an SVG circle with stroke-dashoffset animation.
 */
const CircularProgress: React.FC<{ pct: number }> = ({ pct }) => {
  const size = 36;
  const stroke = 3.5;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (pct / 100) * circumference;

  return (
    <div className="flex-shrink-0 relative" style={{ width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        {/* Background circle */}
        <circle
          cx={size / 2} cy={size / 2} r={radius}
          fill="none" stroke="#bfdbfe" strokeWidth={stroke}
        />
        {/* Progress arc */}
        <circle
          cx={size / 2} cy={size / 2} r={radius}
          fill="none" stroke="#2563eb" strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          style={{
            transform: "rotate(-90deg)",
            transformOrigin: "50% 50%",
            transition: "stroke-dashoffset 0.5s ease",
          }}
        />
      </svg>
      {/* Percentage text in the center */}
      <span
        className="absolute inset-0 flex items-center justify-center text-blue-600 font-semibold"
        style={{ fontSize: 9 }}
      >
        {Math.round(pct)}%
      </span>
    </div>
  );
};

/**
 * Floating pill at the bottom-right that shows when a data import is running
 * in the background. Visible on every page so the user can navigate freely.
 *
 * States:
 *   running   → circular progress with percentage + label
 *   completed → green check + counts + "View" link
 *   failed    → red X + "View details" link
 *
 * Auto-dismisses 30s after completion. User can also click X to dismiss early.
 */
const BackgroundImportBanner: React.FC = () => {
  const { activeImport, dismiss } = useImportProgress();
  const navigate = useNavigate();

  if (!activeImport) return null;

  const isRunning = activeImport.status === "running" || activeImport.status === "pending_async";
  const isCompleted = activeImport.status === "completed";
  const isFailed = activeImport.status === "failed";

  const pct = activeImport.progressPct ?? 0;
  const processed = activeImport.processedRows ?? 0;
  const total = activeImport.totalRows ?? 0;

  const goToImport = () => {
    dismiss();
    navigate("/settings", { state: { section: "import", importStep: "history" } });
  };

  return (
    <div className="fixed bottom-6 right-6 z-50" style={{ animation: "bgImportSlideIn 0.3s ease" }}>
      <div className={`
        flex items-center gap-3 px-4 py-3 rounded-xl shadow-lg border backdrop-blur-sm
        ${isRunning ? "bg-blue-50/95 border-blue-200" : ""}
        ${isCompleted ? "bg-emerald-50/95 border-emerald-200" : ""}
        ${isFailed ? "bg-red-50/95 border-red-200" : ""}
      `} style={{ minWidth: 280, maxWidth: 420 }}>

        {/* Icon / Progress */}
        {isRunning && <CircularProgress pct={pct} />}
        {isCompleted && (
          <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full bg-emerald-100">
            <svg className="w-5 h-5 text-emerald-600" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          </div>
        )}
        {isFailed && (
          <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full bg-red-100">
            <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
        )}

        {/* Text */}
        <div className="flex-1 min-w-0">
          {isRunning && (
            <>
              <p className="text-sm font-medium text-blue-800 truncate">{activeImport.label}</p>
              <p className="text-[11px] text-blue-500">
                {pct >= 100
                  ? "Finalizing — checking duplicates..."
                  : total > 0
                    ? `${processed.toLocaleString()} / ${total.toLocaleString()} rows processed`
                    : "Processing in background..."}
              </p>
            </>
          )}
          {isCompleted && (
            <>
              <p className="text-sm font-medium text-emerald-800">Import complete</p>
              <p className="text-[11px] text-emerald-600">
                {activeImport.type === "zip"
                  ? <>{activeImport.createdRows ?? 0} uploaded
                      {(activeImport.skippedRows ?? 0) > 0 && `, ${activeImport.skippedRows} skipped`}
                      {(activeImport.errorRows ?? 0) > 0 && `, ${activeImport.errorRows} failed`}
                    </>
                  : <>{activeImport.createdRows ?? 0} created, {activeImport.mergedRows ?? 0} merged
                      {(activeImport.errorRows ?? 0) > 0 && `, ${activeImport.errorRows} failed`}
                    </>
                }
              </p>
            </>
          )}
          {isFailed && (
            <>
              <p className="text-sm font-medium text-red-800">Import failed</p>
              <p className="text-[11px] text-red-500">Check job history for details</p>
            </>
          )}
        </div>

        {/* Actions */}
        {(isCompleted || isFailed) && (
          <button
            onClick={goToImport}
            className={`flex-shrink-0 text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors cursor-pointer
              ${isCompleted ? "bg-emerald-600 text-white hover:bg-emerald-700" : ""}
              ${isFailed   ? "bg-red-600 text-white hover:bg-red-700" : ""}
            `}
          >
            View
          </button>
        )}

        {/* Dismiss X */}
        <button
          onClick={dismiss}
          className="flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-full hover:bg-black/5 transition-colors"
          title="Dismiss"
        >
          <svg className="w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default BackgroundImportBanner;
